import { isExitActive } from '@/features/order/order-form/lib/exit-active'
import { buildExitBracketLegs } from '@/features/order/order-form/lib/exit-bracket'
import { resolveBaseCurrency } from '@/features/order/order-form/lib/instrument-resolution'
import {
  baseCurrencyHint,
  buildRelativePrice,
  dedupeTargets,
} from '@/features/order/order-form/lib/submitters/shared'
import type { SubmitterContext } from '@/features/order/order-form/lib/submitters/types'
import { toast } from '@/lib/toast'
import { randomUUID } from '@/lib/utils'
import type { useCreateOrderBatch } from '@/service/execution.hook'
import type { OrderFormCollapsibleConfig } from '@/state/order'
import { Denomination, OrderSide, OrderType } from '@/types/generated/common/trading'
import type { Instrument } from '@/types/generated/common/types'
import { NewOrderParams } from '@/types/generated/execution'

export type SubmitLimitMarketDeps = {
  instruments: Instrument[]
  instrumentMap: Map<string, Instrument>
  isMarketOrder: boolean
  supportsTriggerOrders: boolean
  getCollapsibleConfig: () => OrderFormCollapsibleConfig
  createOrderBatch: ReturnType<typeof useCreateOrderBatch>['mutate']
}

// Submit Limit/Market order
export function submitLimitMarketOrder(ctx: SubmitterContext, deps: SubmitLimitMarketDeps) {
  const { t, form } = ctx
  const {
    instruments,
    instrumentMap,
    isMarketOrder,
    supportsTriggerOrders,
    getCollapsibleConfig,
    createOrderBatch,
  } = deps
  if (!ctx.validateSubmissionRows()) return
  const rawValues = ctx.getRawValues()
  const rows = ctx.getSubmissionRows()
  const items = rows.map(({ item }) => item)
  const timeInForce = form.getValues('timeInForce')
  const reduceOnly = form.getValues('reduceOnly')
  const postOnly = form.getValues('postOnly')

  if (rows.length === 0) {
    toast.error(t('selectAccountFirst'), { position: 'bottom-right' })
    return
  }

  // Validate all items have amounts
  const hasInvalidAmount = rows.some(({ rawItem }) => {
    const amount = rawItem.amount
    return !amount || amount <= 0
  })

  if (hasInvalidAmount) {
    toast.error(t('enterValidAmountAllItems'), { position: 'bottom-right' })
    return
  }

  const priceAnchor = form.getValues('priceAnchor') ?? 'price'
  const priceOffsetUnit = form.getValues('priceOffsetUnit') ?? 'quote_value'

  if (!isMarketOrder && priceAnchor === 'price' && rawValues.price <= 0) {
    toast.error(t('enterValidPrice'), { position: 'bottom-right' })
    return
  }

  ctx.setIsSubmitting(true)
  const isRelativeLimit = !isMarketOrder && priceAnchor !== 'price'

  // Build order params for each item
  const orderParamsList = rows.map(({ item, rawItem, account }) => {
    const amount = rawItem.amount ?? 0
    const amountUnit = item.amountUnit || rawItem.amountUnit || ''

    // Determine denomination based on amountUnit
    // Options always use BASE denomination (InputWithContract shows base ticker only)
    const baseCurrency = resolveBaseCurrency(
      instrumentMap,
      item.symbol,
      baseCurrencyHint(item.baseCurrency)
    )
    const isBaseAmount = item.itemType === 'option' || amountUnit === baseCurrency
    const denomination = isBaseAmount
      ? Denomination.DENOMINATION_BASE
      : Denomination.DENOMINATION_QUOTE

    // For options with contractSize > 1 (e.g. TRX: 1 contract = 10,000 TRX),
    // the UI input is in contracts — multiply to get raw quantity for backend
    const optionContractSize =
      item.itemType === 'option'
        ? Math.max(1, Number(instruments.find((i) => i.symbol === item.symbol)?.contractSize) || 1)
        : 1
    const effectiveAmount = amount * optionContractSize

    return {
      accountId: account.accountId,
      orderSide: item.side === 'buy' ? OrderSide.ORDER_SIDE_BUY : OrderSide.ORDER_SIDE_SELL,
      symbol: item.symbol,
      quantity: effectiveAmount.toString(),
      orderType: isMarketOrder ? OrderType.ORDER_TYPE_MARKET : OrderType.ORDER_TYPE_LIMIT,
      price: isMarketOrder || isRelativeLimit ? undefined : rawValues.price.toString(),
      relativePrice: isRelativeLimit
        ? buildRelativePrice(priceAnchor, priceOffsetUnit, rawValues.price.toString())
        : undefined,
      denomination,
      reduceOnly: reduceOnly || undefined,
      postOnly: isMarketOrder ? undefined : postOnly || undefined,
      tif: isMarketOrder ? undefined : timeInForce,
    }
  })

  // Attach reduce-only exit-bracket legs (TP/SL) from the Exit Condition
  // editor when the bracket is armed. Restricted to single-instrument orders
  // — one TP/SL price can't apply across a multi-symbol basket. This is the
  // simple limit/market path; TWAP / limit-chase / trigger-algo orders route
  // elsewhere and don't carry brackets (the UI guards the section in those
  // modes). See buildExitBracketLegs / exit-active.
  // One correlation id per submission: stamped on every order in the batch and reused as the
  // optimistic submission id, so a validation error echoing it (via order_params.correlation_id)
  // resolves this exact pending order instead of the latest-for-symbol heuristic.
  const lmSubmissionId = randomUUID()
  const stampCorrelation = (o: NewOrderParams): NewOrderParams => ({
    ...o,
    correlationId: lmSubmissionId,
  })

  const orderBatch: NewOrderParams[] = orderParamsList.map(stampCorrelation)
  const collapsibleConfig = getCollapsibleConfig()
  if (orderParamsList.length === 1 && isExitActive(collapsibleConfig) && supportsTriggerOrders) {
    orderBatch.push(
      ...buildExitBracketLegs(orderParamsList[0], items[0].side as 'buy' | 'sell', {
        takeProfit: collapsibleConfig?.priceAbove,
        stopLoss: collapsibleConfig?.priceBelow,
      }).map(stampCorrelation)
    )
  }

  const symbol = items[0]?.symbol || ''
  const lmTargets = dedupeTargets(orderParamsList)
  // Snapshot the selection now, not in onSuccess — it can change while the request is in flight.
  const { instruments: lmInstruments, options: lmOptions } = ctx.getSelection()

  // Execute batch order with callbacks
  createOrderBatch(orderBatch, {
    onSuccess: () => {
      ctx.triggerOptimistic(lmSubmissionId, lmTargets, lmInstruments, lmOptions)
      // No success toast — the WS order response notification already shows
      // one for fills/rejects (notification-manager.tsx); a resting order is
      // visible in the open-orders table without needing a toast too.
      ctx.resetFormAfterOrder()
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : t('orderSubmissionFailed'), {
        symbol,
        position: 'bottom-right',
      })
      ctx.setIsSubmitting(false)
    },
  })
}
